import type {IAnswers, IQuestion} from '~/utils/fixtures/QnA.fixtures';
import {
  IncludeRelationships, type IndexQuestionCollectionResourceDTO, type QuestionDTO,
} from '~/utils/services/api/ms-qna/ms-qna.dto';
import {
  getAnswers, getQuestion, getQuestions, postAnswer, postQuestion, voteOnAnswer,
} from '~/utils/services/api/ms-qna/ms-qna.requests';
import {rejectQueryFn} from '~/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generateKey = (prefix: string, params: any) => {
  return prefix + '__' + Object.keys(params).map((key) => params[key]).join('-');
};

export interface IQuestionPaginated {
  data: IQuestion[]
  meta: { total: number }
}

export interface GetProductQuestionsOptions {
  articleId: string;
}

export interface GetProductAnswersCountOptions {
  articleId: string;
}

export interface GetQuestionOptions {
  id: string;
}

export interface CreateQuestionOptions {
  articleId: string;
  question: string;

  categoryId: string;
  articleName: string;

  brandId: string;
  userPicture: string;
  sku: string;
}

export interface CreateAnswerOptions {
  questionId: string;
  answer: string;
  videos?: string[];
  urls?: string[];
  images?: string[];
}

export interface VoteAnswerOptions {
  answerId: string;
  vote: '1' | '0';
}

const QUESTIONS_IN_PDP = 4;

export const useGetProductQuestionsPDP = (params: GetProductQuestionsOptions) => {
  return useAsyncData(
    generateKey('use-product-questions', params),
    () => getQuestions({
      'filter[article_id]': params.articleId,
      'include': IncludeRelationships.Answers,
      'page[size]': QUESTIONS_IN_PDP,
    }),
    {
      transform: questionPaginatedMapper,
    },
  );
};

export const useGetProductQuestions = (params: GetProductQuestionsOptions) => {
  return useAsyncData(
    generateKey('use-product-questions', params),
    () => getQuestions({
      'filter[article_id]': params.articleId,
      'include': IncludeRelationships.Answers,
      'page[size]': 100,
    }),
    {
      transform: questionPaginatedMapper,
    },
  );
};

export const useGetQuestion = (params: GetQuestionOptions) => {
  return useAsyncData(
    generateKey('use-product-questions', params),
    () => getQuestion(params.id, {include: IncludeRelationships.Answers}),
    {
      transform: (input) => questionMapper(input.data),
    },
  );
};

export const useCreateQuestion = (params: CreateQuestionOptions) => {
  return useAsyncData(
    generateKey('use-create-question', params),
    () => {
      const {userId, userName} = useCpUser();
      if (userId && userName.value) {
        return postQuestion({
          user_id: userId,
          article_id: params.articleId,
          category_id: params.categoryId,
          question: params.question,
          user_name: userName.value,
          article_name: params.articleName,
          brand_id: params.brandId,
          user_picture: params.userPicture,
          send_notification: true,
          sku: params.sku,
        });
      } else {
        throw rejectQueryFn();
      }
    },
  );
};

export const useGetAccountQuestions = () => {
  return useAsyncData(
    generateKey('use-get-account-questions', {}),
    () => {
      const {userId} = useCpUser();
      if (userId) {
        return getQuestions({
          'filter[user_id]': userId,
          'include': IncludeRelationships.Answers,
          'page[size]': 100,
        });
      } else {
        return rejectQueryFn();
      }
    },
    {
      transform: questionPaginatedMapper,
    },
  );
};

export const useGetAccountQuestionsCount = () => {
  return useAsyncData(
    generateKey('use-get-account-questions-count', {}),
    async (): Promise<{ total: number }> => {
      const {userId} = useCpUser();
      if (userId) {
        const res = await getQuestions({
          'filter[user_id]': userId,
          'page[size]': 1,
        });
        return ({total: res.meta?.total});
      } else {
        return rejectQueryFn();
      }
    },
  );
};

export const useGetAccountAnswersCount = () => {
  return useAsyncData(
    generateKey('use-get-account-answers-count', {}),
    async (): Promise<{ total: number }> => {
      const {userId} = useCpUser();
      if (userId) {
        const res = await getAnswers({
          'filter[user_id]': userId,
          'page[size]': 1,
        });
        return ({total: res.meta?.total});
      } else {
        return rejectQueryFn();
      }
    },
  );
};

export const useGetProductAnswersCount = (params: GetProductAnswersCountOptions) => {
  return useAsyncData(
    generateKey('use-get-product-answers-count', params),
    async (): Promise<{ total: number }> => {
      const res = await getAnswers({
        'filter[article_id]': params.articleId,
        'page[size]': 1,
      });
      return ({total: res.meta?.total});
    },
  );
};

export const useCreateAnswer = (params: CreateAnswerOptions) => {
  return useAsyncData(
    generateKey('use-create-answer', params),
    () => {
      const {userId, userName} = useCpUser();
      if (userId && userName.value) {
        return postAnswer({
          user_id: userId,
          user_name: userName.value,
          question_id: params.questionId,
          answer: params.answer,
          videos: params.videos,
          urls: params.urls,
          images: params.images,
        });
      } else {
        throw rejectQueryFn();
      }
    },
  );
};

export const useVoteAnswer = (params: VoteAnswerOptions) => {
  return useAsyncData(
    generateKey('use-create-answer', params),
    () => {
      const {userId, userName} = useCpUser();
      if (userId && userName.value) {
        return voteOnAnswer(params.answerId, {
          user_id: userId,
          vote: params.vote,
        });
      } else {
        throw rejectQueryFn();
      }
    },
  );
};


const questionMapper = (q: QuestionDTO): IQuestion => {
  const PROFILE_PICTURES_URL = 'https://www.cyberpuerta.mx/out/pictures/myprofile/';
  const userPic = q.attributes.user_picture ?? 'https://www.cyberpuerta.mx/out/pictures/myprofile/no-pic.jpg';
  return {
    id: q.id,
    body: q.attributes.question,
    username: q.attributes.user_name,
    date: new Date(q.attributes.created_at).toLocaleDateString(),
    answerCount: q.attributes.answers_count ?? 0,
    status: q.attributes.status,
    answers: q.relationships?.answers?.map<IAnswers>((a) => {
      return {
        id: a.id,
        isBestAnswer: a.attributes?.best_answer === 1,
        userName: a.attributes.user_name,
        userId: a.attributes.user_id,
        body: a.attributes.answer,
        date: new Date(a.attributes.created_at).toLocaleDateString(),
        attachments: {
          image: a.attributes?.attachments?.filter((a) => a.type === 'image').map((a) => a.url) ?? [],
          video: a.attributes?.attachments?.filter((a) => a.type === 'youtube')
            .map((a) => getYouTubeVideoId(a.url) ?? '') ?? [],
          urls: a.attributes?.attachments?.filter((a) => a.type === 'url').map((a) => a.url) ?? [],
        },
        status: a.attributes.status,
        positiveVotes: a.attributes?.positive_votes?.toString(),
        negativeVotes: a.attributes?.negative_votes?.toString(),
        // TODO: Check with Hector
        verifiedPurchase: false,
        userPic: a.attributes.user_picture,
      };
    }) ?? [],
    totalAnswers: q.attributes.answers_count,
    timestamp: new Date(q.attributes.created_at).getTime(),
    userId: q.attributes.user_id,
    articleId: q.attributes.article_id,
    sendNotification: q.attributes.send_notification.toString(),
    userPic: userPic.includes(PROFILE_PICTURES_URL) ? userPic : `${PROFILE_PICTURES_URL}${userPic}`,
  };
};

const questionPaginatedMapper = (input: IndexQuestionCollectionResourceDTO): IQuestionPaginated => {
  return {
    meta: {
      total: input.meta.total,
    },
    data: input?.data.map(questionMapper),
  };
};

const getYouTubeVideoId = (url: string) => {
  // eslint-disable-next-line max-len
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};
